import { motion } from "framer-motion";

const STEPS = [
    {
        no: "01",
        title: "Discover",
        body: "Understand the business, audience, competitors and opportunities.",
    },
    {
        no: "02",
        title: "Strategy",
        body: "Define positioning, messaging, roadmap and execution plan.",
    },
    {
        no: "03",
        title: "Create",
        body: "Build the brand identity, website, content and creative assets.",
    },
    {
        no: "04",
        title: "Launch",
        body: "Execute campaigns, launch products, activate marketing and measure performance.",
    },
    {
        no: "05",
        title: "Scale",
        body: "Optimize, automate and scale through data, AI and continuous growth.",
    },
];

const ease = [0.22, 1, 0.36, 1];

export default function Process() {
    return (
        <section
            id="services"
            data-testid="process-section"
            className="bg-obsidian text-bone relative py-24 lg:py-32 grain"
        >
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative z-10">
                {/* Heading */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16 lg:mb-24">
                    <div className="max-w-3xl">
                        <div className="text-xs uppercase tracking-[0.22em] font-bold text-bone/50">
                            (Our Approach / 01)
                        </div>
                        <h2 className="mt-4 font-display font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl">
                            Our approach.
                        </h2>
                    </div>
                    <p className="max-w-md font-serif italic text-xl lg:text-2xl text-bone/70">
                        Five steps. One methodology. Built to grow brands from
                        seed to scale.
                    </p>
                </div>

                {/* Steps — 5 cols desktop / 2 cols tablet / stacked mobile */}
                <div
                    data-testid="process-grid"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 border-t border-bone/15"
                >
                    {STEPS.map((s, i) => {
                        const isLast = i === STEPS.length - 1;
                        const lgRight = !isLast ? "lg:border-r" : "";
                        const smRight =
                            i % 2 === 0 ? "sm:border-r" : "sm:border-r-0";
                        return (
                            <motion.article
                                key={s.no}
                                data-testid={`process-step-${s.title.toLowerCase()}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{
                                    duration: 0.7,
                                    ease,
                                    delay: i * 0.06,
                                }}
                                className={`group relative px-6 lg:px-8 py-14 lg:py-20 border-bone/15 ${smRight} ${lgRight} border-b sm:border-b lg:border-b-0 last:border-b-0 transition-colors duration-[280ms] ease-out hover:bg-bone/[0.025]`}
                            >
                                {/* Step number */}
                                <div className="flex items-center gap-4">
                                    <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-bone/45 transition-colors duration-[280ms] group-hover:text-[#A8C48A]">
                                        {s.no}
                                    </span>
                                    <span className="h-px w-8 bg-bone/25 transition-all duration-[280ms] ease-out group-hover:w-14 group-hover:bg-[#A8C48A]" />
                                </div>

                                {/* Heading */}
                                <h3 className="mt-10 lg:mt-14 font-display font-black uppercase tracking-tight leading-[0.95] text-3xl lg:text-[2rem] transition-transform duration-[280ms] ease-out group-hover:-translate-y-[3px]">
                                    {s.title}
                                </h3>

                                {/* Body */}
                                <p className="mt-6 text-sm lg:text-[15px] text-bone/65 leading-relaxed max-w-[26ch] transition-colors duration-[280ms] group-hover:text-bone/85">
                                    {s.body}
                                </p>
                            </motion.article>
                        );
                    })}
                </div>

                {/* Bottom CTA */}
                <div className="mt-16 lg:mt-24 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs uppercase tracking-[0.2em] font-bold text-bone/55">
                    <span>
                        One methodology, applied consistently across every
                        engagement.
                    </span>
                    <a
                        href="#contact"
                        data-testid="process-cta"
                        className="link-underline text-bone"
                    >
                        Start with Discover →
                    </a>
                </div>
            </div>
        </section>
    );
}
